import OAuth2State from './oauth2_state';
import Event from '../../../utils/event';
import Strings from '../../../utils/strings';
import {Check, TimeSpan} from "@sparkware/app-utils";
import DOM from '../../../utils/dom';
import { SYSTEM_EVENTS} from '../../../events/events';
import { KICK_REASONS, KICK_TYPE} from '../../player_messages_service/player_messages_consts';
import Token from '../tokens/token';
import { OAUTH2_CSS} from '../oauth_const';
import OptionsComponentController from '../../../components/options/options_component_controller';
import UserActivityDetector from './user_activity_detector';
import { MESSAGE_TYPE } from '../../../components/message/message_consts';
import EmptyToken from '../tokens/empty_token';
import {PRIORITY_LEVEL} from '../../overlay_manager/overlay_manager_levels';
import _ from '../../../utils/underscore';
import {LOGOUT_REASONS} from '../oauth_const';


export const REFRESH_TOKEN_REASONS = {
    on_init_complete: 'on_init_complete',
    on_user_activity: 'on_user_activity',
    on_user_discard_idle_popup: 'on_user_discard_idle_popup',
    user_set_custom_session: 'user_set_custom_session',
    on_app_resume: 'on_app_resume',
    try_remember_me: 'try_remember_me',
};

const GENERAL_KICK_TRANSLATION_KEY = 'kick_reason_general';

export default class OAuth2AuthorizedState extends OAuth2State {
    static CLASS = 'OAuth2AuthorizedState';

    _currentToken = null;
    _stateChangedCallback = null;

    _backendProxy = null;
    _oauth2StateFactory = null;
    _serviceFactory = null;
    _userActivityDetector = null;
    _logoutUserTimer = null;
    _logoutUserCancelTimer = null;
    _kickInactiveUserTimer = null;
    _inactivityTimeoutSettingsPersister = null;
    _getSessionTimerUrl = null;

    init(params) {
        super.init(params);

        this._stateChangedCallback = params.stateChangedCallback;
        this._backendProxy = params.backendProxy;
        this._oauth2StateFactory = params.oauth2StateFactory;
        this._serviceFactory = params.serviceFactory;
        this._inactivityTimeoutSettingsPersister = params.inactivityTimeoutSettingsPersister;
        this._getSessionTimerUrl = params.getSessionTimerUrl;


        this._currentToken = params.token;

        this._subscribe();


        DOM.addClass(DOM.getBody(), OAUTH2_CSS.AUTHORIZED_USER_CLASS);
    }

    onInitComplete(fromOnInitCompleteEvent) {
        this._userActivityDetector = UserActivityDetector.create({
            serviceFactory: this._serviceFactory,
            userActivityDetectionInterval: TimeSpan.fromMilliseconds(Math.max(1000, this._accessTokenLifeTimeMS() / 100))
        });

        if (fromOnInitCompleteEvent) {
            if(this._serviceFactory.regulationService().shouldRefreshTokenOnAppOpen()) {
                if(!this._serviceFactory.queryStringDetectorService().isInAutologin()) {
                    this.refreshToken(REFRESH_TOKEN_REASONS.on_init_complete); // we get to this line when the user has a token in the local storage and he refresh the browser page (F5)
                }
            }
        } else {
            this._startKickInactiveUserTimer(); // otherwise it means that the user just logged in
        }
    }

    static forNewLogin(params) {

        let authorizedState = OAuth2AuthorizedState.create(params);
        authorizedState._updateNewLoginData();
        return authorizedState;
    }

    static forExistingToken(params) {
        return OAuth2AuthorizedState.create(params);
    }

    isAuthorized() {
        return true;
    }

    _sessionEventKickSubscription = null;
    _appResumeSubscription = null;
    _subscribe() {
        this._subscribeToLocalStorageChanged();
        this._sessionEventKickSubscription = Event.on(SYSTEM_EVENTS.SESSION_EVENT_KICK, this._onKickEvent, this);
        this._appResumeSubscription = Event.on(SYSTEM_EVENTS.APP_RESUMED, this._onAppResumed, this);

    }

    _unsubscribe() {
        this._unsubscribeFromLocalStorageChanged();
        if(this._sessionEventKickSubscription) {
            this._sessionEventKickSubscription.off();
            this._sessionEventKickSubscription = null;
        }

        if(this._appResumeSubscription) {
            this._appResumeSubscription.off();
            this._appResumeSubscription = null;
        }
    }

    _localStorageChangedSubscription = null;
    _subscribeToLocalStorageChanged() {
        this._localStorageChangedSubscription = Event.on(SYSTEM_EVENTS.LOCAL_STORAGE_CHANGED, this._onLocalStorageChanged, this);
    }
    _unsubscribeFromLocalStorageChanged() {
        if(this._localStorageChangedSubscription) {
            this._localStorageChangedSubscription.off();
            this._localStorageChangedSubscription = null;
        }
    }

    dispose() {
        this._unsubscribe();

        if (this._userActivityDetector) {
            this._userActivityDetector.dispose();
        }

        this._killTimers();

    }

    _updateNewLoginData() {
        this._currentToken.updatePostLoginData();
        this._sendOptionsConfig();
    }

    _sendAnalytics(action, label) {
        let analyticsData = {
            category: 'oauth2_authorized_state',
            action: action
        };

        if (!_.isNullOrUndefined(label)) {
            analyticsData.label = label;
        }

        this._serviceFactory.analyticsService().sendEvent(analyticsData);
    }

    _killTimers() {
        if (this._kickInactiveUserTimer) {
            this._kickInactiveUserTimer.dispose();
        }

        if (this._logoutUserTimer) {
            this._logoutUserTimer.dispose();
        }
        if (this._logoutUserCancelTimer) {
            this._logoutUserCancelTimer.dispose();
        }
    }

    _accessTokenLifeTimeMS() {
        return this._inactivityTimeoutSettingsPersister.getAccessTokenLifeTime().totalMilliseconds;
    }

    _timeSinceLastUserActivityMS() {
        return this._userActivityDetector.getTimeSinceLastUserActivity();
    }

    _timeBeforeWhichTheUserShouldBeNotifiedMS() {
        const defaultValue = TimeSpan.fromMinutes(2).totalMilliseconds;
        if (defaultValue >= this._accessTokenLifeTimeMS()) { // this is done to support small access token lifetime values in development and testing
            return 0.2 * this._accessTokenLifeTimeMS();
        } else {
            return defaultValue;
        }
    }
    _timeAfterWhichTheIdlePopUpMessageShouldBeShownMS() {
        return Math.max(0, this._accessTokenLifeTimeMS() - this._timeSinceLastUserActivityMS() - this._timeBeforeWhichTheUserShouldBeNotifiedMS());
    }

    _startKickInactiveUserTimer() {
        this._killTimers();
        const timeAfterWhichTheIdlePopUpMessageShouldBeShown = this._timeAfterWhichTheIdlePopUpMessageShouldBeShownMS();
        this._sendAnalytics('timeAfterWhichTheIdlePopUpMessageShouldBeShownInMS:' + timeAfterWhichTheIdlePopUpMessageShouldBeShown);

        this._kickInactiveUserTimer = this._serviceFactory.observableFactoryService().fromTimeout(timeAfterWhichTheIdlePopUpMessageShouldBeShown)
            .forEach(() => {
                const timeSinceLastUserActivity = this._timeSinceLastUserActivityMS();
                this._sendAnalytics('timeSinceLastUserActivityInMS:' + timeSinceLastUserActivity);

                if (timeSinceLastUserActivity < timeAfterWhichTheIdlePopUpMessageShouldBeShown) {
                    this.refreshToken(REFRESH_TOKEN_REASONS.on_user_activity);
                } else {
                    this._logoutUserTimer = this._startLogoutTimer();
                    this._logoutUserCancelTimer = this._startLogoutTimerCanceler();
                    this._showIdlePopUpMessage(() => {
                        this._serviceFactory.analyticsService().sendOrbitUserFlowEvent({
                            category: 'InactivityTimeoutPopUp',
                            action: 'OK',
                            label: 'PreDisconnect'
                        });
                
                        // Clicking on the OK button will trigger an overlay close event but that happens too late.
                        // This is why here explicitly we let the UserActivityDetector know about this user activity.
                        this._userActivityDetector.setLastTimeUserPerformedAction();
                        this._killTimers();
                        this.refreshToken(REFRESH_TOKEN_REASONS.on_user_discard_idle_popup);
                    }, () => {
                        this._serviceFactory.analyticsService().sendOrbitUserFlowEvent({
                            category: 'InactivityTimeoutPopUp',
                            action: 'Close',
                            label: 'PreDisconnect'
                        });
                    });
                }
            });
    }

    _showIdlePopUpMessage(onClickCallback, onCloseCallback) {
        let messageTextTranslation = new Strings(this._serviceFactory.languageService().translation('mdp_inactivity'));

        let messageText = messageTextTranslation.injectIntoCurly({
            timer: 2
        });


        let okButton = {
            callback: onClickCallback
        };

        let message = {
            text: messageText,
            type: MESSAGE_TYPE.WARNING,
            buttons: [
                okButton
            ],
            closeCallback: onCloseCallback
        };

        this._serviceFactory.analyticsService().sendOrbitUserFlowEvent({
            category: 'InactivityTimeoutPopUp',
            action: 'Show',
            label: 'PreDisconnect'
        });

        this._showMessage(message);
    }

    _startLogoutTimer() {
        return this._serviceFactory.observableFactoryService().fromTimeout(this._timeBeforeWhichTheUserShouldBeNotifiedMS())
            .forEach(() => {
                this._serviceFactory.analyticsService().sendOrbitUserFlowEvent({
                    category: 'InactivityTimeoutPopUp',
                    action: 'Show',
                    label: 'PostDisconnect'
                });

                this._switchToAnonymousState({logoutReason: LOGOUT_REASONS.USER_IDLE});
                Event.fire(SYSTEM_EVENTS.TOKEN_LIFETIME_EXPIRED);
                this._serviceFactory.orbitComponentRenderService().showDisconnectPopup();
            });
    }

    _startLogoutTimerCanceler() {
        // The purpose of this timer is to close the idle message pop up and refresh token in this tab
        // when the user clicks on the OK button in another browser tab.
        // If we don't do this then the user will be logged out in this browser tab even if he clicked OK button in the other browser tab.
        return this._serviceFactory.observableFactoryService().fromInterval(1000).forEach(() => {
            if (this._timeSinceLastUserActivityMS() < this._timeAfterWhichTheIdlePopUpMessageShouldBeShownMS()) {

                this._sendAnalytics('idle_popup_canceled');

                this._killTimers();
                Event.fire(SYSTEM_EVENTS.CLOSE_OVERLAY, {priority: PRIORITY_LEVEL.MESSAGE});
                this.refreshToken(REFRESH_TOKEN_REASONS.on_user_activity);
            }
        });
    }



    _onLocalStorageChanged() {
        if (!Token.isInStorage()) { // it means it was removed so we need to switch to anonymous state
            this._switchToAnonymousState({logoutReason: LOGOUT_REASONS.ACCESS_TOKEN_REMOVED});
            EmptyToken.create().delete();
            Event.fire(SYSTEM_EVENTS.OAUTH_REMOVE_FROM_LOCAL_STORAGE);
        } else {
            if (this._currentToken.getAccessTokenField() !== Token.getAccessTokenFieldFromLocalStorage()) {
                //it means that the token was changed in another browser instance
                // probably due to an autologin
                this._currentToken = Token.fromStorage();
            }
        }
    }

    _onKickEvent(kickParams) {
        if (kickParams.KickType !== KICK_TYPE.FULL_KICK) {
            return;
        }

        this._switchToAnonymousState({logoutReason: LOGOUT_REASONS.USER_KICKED, kickReason: kickParams.KickReasonId});

        if (this._serviceFactory.visibilityDetectorService().isActive()) {
            let textKey = null;
            if (this._getIfKnownKickReason(kickParams.KickReasonId)) {
                textKey = 'kick_reason_' + kickParams.KickReasonId;
            } else {
                textKey = GENERAL_KICK_TRANSLATION_KEY;
            }

            this._serviceFactory.orbitComponentRenderService().showDefaultKickPopup(textKey);
        }
    }

    _getIfKnownKickReason(reasonId) {
        return KICK_REASONS.includes(reasonId);
    }

    _lastRefreshOnAppResume = null;
    _onAppResumed() {
        if (Token.isInStorage()) {
            if(!this._serviceFactory.queryStringDetectorService().isInAutologin()) {
                this._userActivityDetector.setLastTimeUserPerformedAction();
                if(Check.isNullOrUndefined(this._lastRefreshOnAppResume) || (Date.now() - this._lastRefreshOnAppResume) > 60000) {
                    this._lastRefreshOnAppResume = Date.now();
                    this.refreshToken(REFRESH_TOKEN_REASONS.on_app_resume);
                } else {
                    this._startKickInactiveUserTimer();
                }
            }
        } else {
            this._switchToAnonymousState({logoutReason: LOGOUT_REASONS.ACCESS_TOKEN_REMOVED});
        }
    }


    _switchToAnonymousState(params) {
        Event.fire(SYSTEM_EVENTS.ON_BEFORE_OAUTH_STATE_CHANGE);

        this.dispose();

        
        if (!this._currentToken.hasRememberMe()) {
            this._currentToken.delete();
            this._currentToken = EmptyToken.create();
        }
        
        let newState = this._oauth2StateFactory.createAnonymousState();
        
        this._stateChangedCallback(newState, params);
        
        this._closeAllOverlays();

        return newState;
    }

    _closeAllOverlays() {
        Event.fire(SYSTEM_EVENTS.CLOSE_ALL_OVERLAY);
    }

    _showMessage(message) {
        Event.fire(SYSTEM_EVENTS.MESSAGE_OPEN, message);
    }

    /**
     * Send reminder and other options config to analytics
     * @private
     */
    _sendOptionsConfig() {
        let reminderController = OptionsComponentController.create().load('ReminderSettings');
        reminderController.reportAnalytics();
    }

    tryToRememberMe() {
        //no need to do something
        //we are already logged in
        return Promise.resolve();
    }

    login(credentials) {
        this.throwError('392',
            'No need to execute login! The user is already logged in!',
            { credentials: Object.assign(credentials, { password: '*' }) });
    }

    logout() {
        let executeOnLogout = () => {
            this._switchToAnonymousState({logoutReason: LOGOUT_REASONS.EXPLICIT_LOGOUT});
            // on explicit logout we make sure we clean up token properties from storage
            this._currentToken.delete();
        };

        return this._backendProxy.logout()
            .then(() => {
                executeOnLogout();
            }).catch(() => {
                executeOnLogout();
            });
    }

    autologinWithToken(autologinToken, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId, tokenType, businessCorrelationId) {
        return this._backendProxy.autoLoginWithToken(autologinToken, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId, tokenType, businessCorrelationId)
            .then((tokenResponse) => {
                //temporary disable local storage change notification
                //otherwise we will have duplicated OAUTH_STATE_CHANGE events fired due to local storage changes
                this._unsubscribeFromLocalStorageChanged();
                try {
                    Event.fire(SYSTEM_EVENTS.ON_BEFORE_OAUTH_STATE_CHANGE);
                    this._currentToken = Token.fromNew({
                        tokenData: tokenResponse
                    });
                    this._updateNewLoginData();
                    Event.fire(SYSTEM_EVENTS.LOGIN_SESSION_RESUMED);//cross sell session continuation
                }
                finally {
                    //reenable local storage change notification
                    this._subscribeToLocalStorageChanged();
                }

                Event.fire(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, {loginType: this.getLoginType()});
            })
            .catch(responseError => {
                this._switchToAnonymousState({logoutReason: LOGOUT_REASONS.AUTOLOGIN_FAILED});
                this._currentToken.delete();
                this._handleAutologinError(responseError);
            });
    }

    _handleAutologinError(responseError) {
        Event.fire(SYSTEM_EVENTS.TOUCH_ID_LOGIN_ERROR, responseError.error_code);
        this._serviceFactory.webLoginService().processLoginResponse(responseError);
    }

    getDecodedAccessToken() {
        return this._currentToken.getDecodedAccessToken();
    }

    getTokenAuthorizeHeader() {
        return this._currentToken.getHttpAuthorizationHeader();
    }

    extractTokenProperty(tokenProperty) {
        return this._currentToken.getFieldValue(tokenProperty);
    }

    isVIP() {
        return this.getUserData().accountData.isVIP;
    }

    getUserData() {
        return this._currentToken.getUserData();
    }

    getAccountData() {
        return this._currentToken.getAccountData();
    }

    updateTokenData(filedName, fieldValue) {
        this._currentToken.setFieldValue(filedName, fieldValue);
    }

    getRefreshToken() {
        return this._currentToken.getRefreshToken();
    }

    refreshToken(reason) {
        if (!Token.isInStorage()) {
            return Promise.reject('There is no token in the local storage to refresh');
        }

        this._killTimers();
        let timeSinceLastUserActivity = this._userActivityDetector.getTimeSinceLastUserActivity();
        return this._backendProxy.refreshToken(this._currentToken.getRefreshToken(), {timeSinceLastActivity: timeSinceLastUserActivity, reason: reason})
            .then((tokenData) => {
                this._currentToken = Token.fromNew({
                    tokenData: tokenData
                });
                this._startKickInactiveUserTimer();
                if(reason === REFRESH_TOKEN_REASONS.on_init_complete || reason === REFRESH_TOKEN_REASONS.on_app_resume) {
                    Event.fire(SYSTEM_EVENTS.LOGIN_SESSION_RESUMED);
                }

            })
            .catch(err => {
                this._switchToAnonymousState({logoutReason: LOGOUT_REASONS.REFRESH_TOKEN_FAILED});
                if(!this._serviceFactory.integrationService().isMiniOrbit()) {
                    this._serviceFactory.orbitComponentRenderService().showDefaultKickPopup(GENERAL_KICK_TRANSLATION_KEY);
                }
                Event.fire(SYSTEM_EVENTS.TOKEN_LIFETIME_EXPIRED);
                this._currentToken.delete();

                this.reThrowError('503', 'Failed to refresh token', err);
            });
    }

    /**
     *
     * @returns {Promise.<TimeSpan>}
     */
    getTimeSinceLogin() {
        return this._serviceFactory.http2Service().post(this._getSessionTimerUrl)
            .then(data => {
                return TimeSpan.parse(data.SessionTimer)
            });
    }

    getLoginType() {
        return this.getDecodedAccessToken().loginType;
    }

    getSourceProductPackage() {
        return this.getDecodedAccessToken().sourceProductPackage;
    }

}
